/**
 * Timers are anchored to a wall-clock start timestamp rather than accumulated
 * ticks, so persisting a running timer only means persisting when it started.
 * Everything else is derived from `Date.now()`, which is what lets a timer
 * survive a navigation, a refresh, or the browser being closed and reopened.
 *
 * localStorage (not sessionStorage) precisely because closing the browser must
 * not lose a running rest period.
 */

/** Reads a stored start time in epoch milliseconds, or null. */
export function readStartedAt(key: string): number | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const n = Number(raw);
    // Guard against corrupt values and against clocks that moved backwards.
    if (!Number.isFinite(n) || n <= 0 || n > Date.now() + 60_000) return null;
    return n;
  } catch {
    return null;
  }
}

export function writeStartedAt(key: string, startedAt: number): void {
  try {
    localStorage.setItem(key, String(startedAt));
  } catch {
    // Private-mode storage failures must not interrupt a workout.
  }
}

export function clearStartedAt(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch { /* ignore */ }
}

/** Reads a JSON record, returning null on anything unparseable. */
export function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* ignore */ }
}

export function clearJson(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch { /* ignore */ }
}

// ── Key builders ────────────────────────────────────────────────────────────
// Scoped by session (and exercise) so two workouts never share a timer.

/**
 * Scoped to the session, not the exercise: only one rest period runs at a
 * time, and it has to survive moving on to the next exercise.
 */
export const restTimerKey = (sessionId: string) => `gymcoach_rest_${sessionId}`;

/**
 * The rest window in force, stored alongside the start time. A rest carried
 * into the next exercise is still counting down that *previous* exercise's
 * prescription, so the window cannot be re-read from whatever is on screen.
 */
export const restWindowKey = (sessionId: string) => `gymcoach_restwin_${sessionId}`;

export const drillTimerKey = (sessionId: string) => `gymcoach_drill_t_${sessionId}`;
export const drillActiveKey = (sessionId: string) => `gymcoach_drill_i_${sessionId}`;

export const WORKOUT_TIMER_KEY = 'gymcoach_workout_timer';
